import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import Link from '@mui/material/Link';
import CircularProgress from '@mui/material/CircularProgress';
import axios from 'axios';
import login_image1 from '../image/login_image1.jpg';
import eCosicon from "../icon/eCos-icon.png";
import { ToastContainer, toast } from 'react-toastify';
import { UseAccessLevel } from '../AccessLevelContext';
import { IconButton, InputAdornment, Dialog, DialogTitle, DialogContent, DialogActions } from '@mui/material';
import { Visibility, VisibilityOff } from '@mui/icons-material';
// API
import { loginUser } from '../api/auth';
import { getPlantList } from '../api/plant';

const LoginForm = () => {
  const { refreshUserId } = UseAccessLevel();
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [errors, setErrors] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const navigate = useNavigate();
  const { setAccessLevel } = UseAccessLevel();
  const handleClickShowPassword = () => setShowPassword((prev) => !prev);
  const handleMouseDownPassword = (event) => event.preventDefault();
  const [showPassword, setShowPassword] = useState(false);
  const [expiredUI, setExpiredUI] = useState(false);
  const [expiredDialogOpen, setExpiredDialogOpen] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");



  const validateForm = () => {
    let valid = true;

    if (!username) {
      toast.error('Username is required');
      valid = false;
    }

    if (!password) {
      toast.error('Password is required');
      valid = false;
    } else if (password.length < 8) {
      toast.error('Password must be at least 8 characters');
      valid = false;
    }
    return valid;
  };

  const navigateToPlantlist = async () => {

    try {
      const plantListData = await getPlantList();
      const accessLevel = plantListData.access_level; // Extract accesslevel
      setAccessLevel(accessLevel); // Set globally
      navigate('/PlantList', { state: { userData: plantListData } });
    } catch (error) {
      setErrors({ general: 'Error accessing PlantList. Please try again.' });
      setIsLoading(false);
    }
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (validateForm()) {
      setIsLoading(true);

      try {
        const response = await loginUser(username, password);

        if (response.status === 200 && response.data.access) {
          sessionStorage.setItem('accessToken', response.data.access);
          sessionStorage.setItem('refreshToken', response.data.refreshs);

          axios.defaults.headers.common['Authorization'] =
            `Bearer ${response.data.access}`;

          await refreshUserId(); // 👈 add this line — fetch userId now that token is set

          toast.success('Login successful');
          setTimeout(() => navigateToPlantlist(), 3000);
        } else {
          setErrors({ general: 'Invalid credentials. Please try again.' });
        }

      } catch (err) {
        const backendMessage = err.response?.data?.error;

        if (backendMessage === "Your license has expired") {
          setExpiredUI(true);
          setErrorMessage(backendMessage);
          setExpiredDialogOpen(true);
          return;
        }

        setErrors({ general: "Invalid Username or Password" });
        toast.error("Invalid Username or Password");
      }
      finally {
        setIsLoading(false);
      }
    }
  };

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: '100vh',
        backgroundImage: `url(${login_image1})`,
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        backgroundRepeat: 'no-repeat',
      }}
    >
      <ToastContainer />

      <Box
        sx={{
          p: 4,
          width: { xs: "350px", md: "400px" },
          textAlign: 'center',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          height: '100%',
          borderRadius: 2,
          backgroundColor: 'rgba(255, 255, 255, 0.7)',
          boxShadow: 3,
        }}
      >
        {/* icon */}
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            width: '100%',
            height: 'auto',
            mb: 3,
          }}
        >
          <img
            src={eCosicon}
            alt="Login"
            style={{
              width: '25%',
              height: '25%',
              objectFit: 'cover',
            }}
          />
        </Box>

        {/* Form Section */}
        <Typography
          sx={{
            fontFamily: 'Arial, sans-serif',
            fontWeight: 'bold',
            fontStyle: 'italic',
            fontSize: { xs: "4vw", md: "2vw" },
            color: "black"
          }}
        >
          Welcome
        </Typography>

        <Box
          component="form"
          onSubmit={handleSubmit}
          noValidate
          sx={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            flexDirection: 'column',
            width: '75%',
            height: '50%',
            mt: 1,
            mx: 'auto',
          }}
        >
          <TextField
            label="Username"
            fullWidth
            variant="outlined"
            margin="normal"
            value={username}
            onChange={(e) => setUsername(e.target.value)}
            error={!!errors.username}
            sx={{
              backgroundColor: 'rgba(255, 255, 255, 0.8)',
              borderRadius: 2,
            }}

          />
          <TextField
            label="Password"
            fullWidth
            variant="outlined"
            margin="normal"
            type={showPassword ? 'text' : 'password'}
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            sx={{
              backgroundColor: 'rgba(255, 255, 255, 0.8)',
              borderRadius: 2,
            }}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    onClick={handleClickShowPassword}
                    onMouseDown={handleMouseDownPassword}
                    edge="end"
                  >
                    {showPassword ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />
          {errors.general && (
            <Typography color="error" variant="body2" mt={1}>
              {errors.general}
            </Typography>
          )}
          <Button
            type="submit"
            fullWidth
            variant="contained"
            sx={{
              backgroundColor: 'purple',
              mt: 2,
              mb: 2,
              transition: 'all 0.3s ease-in-out',
              '&:hover': {
                backgroundColor: '#d998ffbb',
                transform: 'scale(1.05)',
                boxShadow: '0 4px 8px rgba(0, 0, 0, 0.3)',
              },
            }}
            disabled={isLoading}
          >
            {isLoading ? <CircularProgress size={24} /> : 'Sign In'}
          </Button>
          <Link
            component="button"
            variant="body2"
            onClick={() => navigate('/Forgotpassword')}
            sx={{ display: 'block', mb: 2 }}
          >
            Forgot password?
          </Link>
        </Box>

        {/* license expired popup UI */}
        <Dialog
          open={expiredDialogOpen}
          onClose={() => setExpiredDialogOpen(false)}
          PaperProps={{
            sx: {
              borderRadius: "16px",
              padding: "1vw",
              width: "32vw",
              boxShadow: "0 8px 24px rgba(0,0,0,0.15)",
            },
          }}
        >
          <DialogTitle
            sx={{
              textAlign: "center",
              color: "#d32f2f",
              fontSize: "1.3vw",
              fontWeight: "bold",
              pb: 0,
            }}
          >
            ⚠ License Expired
          </DialogTitle>

          <DialogContent
            sx={{
              textAlign: "center",
              mt: 1,
              px: 4,
              pb: 1,
              fontSize: "1vw",
              pb: 1.4,
            }}
          >
            <p
              style={{
                fontSize: "1rem",
                color: "#555",
                marginTop: "8px",
                lineHeight: "1.5",
              }}
            >
              Your system license has expired.
              <br />
              To keep your access active, please call <span style={{ fontWeight: "bold", color: "#000" }}> 03-5888 8466</span>. Thank you.
            </p>
          </DialogContent>

          <DialogActions sx={{ justifyContent: "center", pb: 2 }}>
            <Button
              onClick={() => setExpiredDialogOpen(false)}
              variant="contained"
              sx={{
                textTransform: "none",
                fontSize: "0.8vw",
                borderRadius: "8px",
                padding: "8px 20px",
                backgroundColor: "#d32f2f",
                ":hover": { backgroundColor: "#b71c1c" },
              }}
              autoFocus
            >
              Close
            </Button>
          </DialogActions>
        </Dialog>


      </Box>
    </Box>
  );
};

export default LoginForm;